import { AccessToken } from '@shared/api/common/access-token';
import { AccountDetails } from '@shared/api/identity/account-data';
import { RootState } from '../store';
import { createNilReducer, createReducer } from '../core';
import { UserEnterprise } from '@shared/api/identity/models/user-enterprise';

export type AccountState = {
  userId?: string;
  session?: AccountSession;
  deviceId?: string;
  showWelcome: boolean;
};

export function newAccountState(): AccountState {
  return {
    userId: undefined,
    session: undefined,
    showWelcome: false,
  };
}

export type AccountSession = {
  userId: string;
  identityToken: AccessToken;
  accountDetails: AccountDetails;
  editingPhoneNumber?: {
    phoneNumber: string;
    mfaVerificationId: string;
  }
  editingEmail?: {
    email: string;
    approverEmailMasked?: string;
    approverEmail?: string;
    mfaVerificationId?: string;
  }
  enterprises?: UserEnterprise[];
  avatar?: string;
  iproovOptOut?: boolean;
};

export const loadAccountSlice = createReducer((state, payload: AccountState) => {
  state.account = {
    ...newAccountState(),
    ...payload,
  }
});

export const createAccount = createReducer((state, payload: { userId: string; showWelcome: boolean }) => {
  state.account.userId = payload.userId;
  state.account.showWelcome = payload.showWelcome;
});

export const setDeviceId = createReducer((state, payload: { deviceId: string }) => {
  state.account.deviceId = payload.deviceId;
})

export const deleteDeviceId = createNilReducer((state) => {
  state.account.deviceId = undefined;
})

export const clearAccountWelcome = createNilReducer((state) => {
  state.account.showWelcome = false;
});

export const startAccountSession = createReducer(
  (
    state,
    payload: {
      userId: string;
      identityToken: AccessToken;
      accountDetails: AccountDetails;
    },
  ) => {
    state.account.session = {
      ...payload,
    };
  },
);

export const saveAccountDetails = createReducer((state, payload: { accountDetails: AccountDetails }) => {
  if (state.account.session) {
    state.account.session.accountDetails = payload.accountDetails;
  }
});

export const saveAvatar = createReducer((state, payload: { avatar: string }) => {
  if (state.account.session) {
    state.account.session.avatar = payload.avatar;
  }
});

export const saveAccountEnterprises = createReducer((state, payload: { enterprises: UserEnterprise[] }) => {
  if (!state.account.session) {
    return;
  }
  state.account.session.enterprises = payload.enterprises;
});

export const startEditingPhoneNumber = createReducer((state, payload: { phoneNumber: string, mfaVerificationId: string }) => {
  if (!state.account.session) {
    return;
  }
  state.account.session.editingPhoneNumber = {
    phoneNumber: payload.phoneNumber,
    mfaVerificationId: payload.mfaVerificationId,
  }
});

export const updateEditingPhoneNumberMfa = createReducer((state, payload: { mfaVerificationId: string }) => {
  if (!state.account.session?.editingPhoneNumber) {
    return;
  }
  state.account.session.editingPhoneNumber.mfaVerificationId = payload.mfaVerificationId;
});

export const clearEditingPhoneNumber = createNilReducer((state) => {
  if (!state.account.session) {
    return;
  }
  state.account.session.editingPhoneNumber = undefined;
});

export const startAddingEmail = createReducer((state, payload: { email: string, mfaVerificationId: string}) => {
  if (!state.account.session) {
    return;
  }
  state.account.session.editingEmail = {
    email: payload.email,
    mfaVerificationId: payload.mfaVerificationId,
  }
})

export const startEditingEmail = createReducer((state, payload: { email: string, approverEmailMasked?: string }) => {
  if (!state.account.session) {
    return;
  }
  state.account.session.editingEmail = {
    email: payload.email,
    approverEmailMasked: payload.approverEmailMasked ?? undefined,
  }
})

export const putEditingEmailMfa = createReducer((state, payload: { approverEmail: string, mfaVerificationId: string }) => {
  if (!state.account.session?.editingEmail) {
    return;
  }
  state.account.session.editingEmail.approverEmail = payload.approverEmail;
  state.account.session.editingEmail.mfaVerificationId = payload.mfaVerificationId;
})

export const updateEditingEmailMfa = createReducer((state, payload: { mfaVerificationId: string }) => {
  if (!state.account.session?.editingEmail) {
    return;
  }
  state.account.session.editingEmail.mfaVerificationId = payload.mfaVerificationId;
});

export const clearEditingEmail = createNilReducer((state) => {
  if (!state.account.session) {
    return;
  }
  state.account.session.editingEmail = undefined;
});

export const setAccountIproovOptOut = createReducer((state, payload: { optOut: boolean }) => {
  if (!state.account.session) {
    return;
  }
  state.account.session.iproovOptOut = payload.optOut;
});

export const logout = createNilReducer((state) => {
  state.account.session = undefined;
  return state;
});

export const deleteAccount = createNilReducer((state) => {
  state.account.userId = undefined;
  state.account.session = undefined;
  return state;
});

export const selectAccount = () => (state: RootState) => {
  return state.account;
};

export const selectAccountSession = () => (state: RootState) => {
  return state.account.session;
};

export const selectAccountEditingEmail = () => (state: RootState) => {
  return state.account.session?.editingEmail;
};

export const selectAccountEditingPhone = () => (state: RootState) => {
  return state.account.session?.editingPhoneNumber;
};

export const selectDeviceId = () => (state: RootState) => {
  return state.account.deviceId;
}

export const selectUserId = () => (state: RootState) => {
  return state.account.userId;
}
